import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import NiceModal, { useModal } from '@ebay/nice-modal-react';

// Import styles
import { AntModalStyled, ModalContent, HeadingFirstStyled } from '../styles';
import { ModalCloseButton } from '../components/ModalCloseButton';

// Import types
import type { PacketWithPriceAndChannels } from 'types/views/configurator';
import type { CalculatedPriceProduct } from 'types/api/packetsSubscription';

// Import components
import { PriceInfo } from 'components/views/offer_configurator/components/common';
import { PacketsSlider, Slide } from 'components/elements/molecules/PacketsSlider';
import { Packet } from './Packet';

// Import context
import PremiumPacketProvider from 'components/context/premium_packet/PremiumPacketProvider';

// Import hooks
import { useSummaryStore, useCalculatedPrice } from 'components/views/offer_configurator/hooks';

// Import helpers
import { getCalculatedPrice } from 'components/views/offer_configurator/helpers/packets';
import { sendPacketAddToCartEvent } from 'helpers/tracking';

// Import context
import { usePacketsGroupContext } from 'components/context/premium_packet/PacketsGroupProvider';

type Props = {
	provider: string;
	packets: PacketWithPriceAndChannels[];
	type: 'review' | 'summary';
};

export const PacketsGroupModal = NiceModal.create(({ provider, packets, type }: Props) => {
	const modal = useModal();
	const { t } = useTranslation('configurator');
	const [premium, setPremiums] = useSummaryStore(useShallow((state) => [state.premium, state.setPremiums]));
	const calculated = useCalculatedPrice();
	const { packetsUuid, isSubscriptionActive, onPacketSelect, isDisabled } = usePacketsGroupContext();

	const onSelect = useCallback(
		(packet: PacketWithPriceAndChannels, isSelected: boolean, price: CalculatedPriceProduct | undefined) => {
			if (type === 'review') {
				onPacketSelect?.(packet);
			} else if (!isSelected) {
				const packets = premium.filter(({ uuid }) => !packetsUuid.includes(uuid));
				packets.push({ uuid: packet.uuid, price_id: packet.price_id });
				setPremiums(packets);
				sendPacketAddToCartEvent({ item_id: packet.uuid, item_name: packet.title, subtype: packet.subtype, price: price?.current_price });
			} else {
				const packets = premium.filter(({ uuid }) => uuid !== packet.uuid);
				setPremiums(packets);
			}
			modal.remove();
		},
		[premium]
	);

	return (
		<AntModalStyled
			maskClosable={false}
			footer={null}
			width="95%"
			closable={false}
			onCancel={modal.remove}
			visible={modal.visible}
			centered
			fullWidth
		>
			<ModalCloseButton closeModal={modal.remove} withPadding />
			<ModalContent maxWidth="100%" gap={2}>
				<HeadingFirstStyled>{t('modal_select_packet_variant_title', { provider })}</HeadingFirstStyled>

				<PacketsSlider initialSlide={0} padding={false}>
					{packets.map((packet) => {
						const isSelected = premium.some(({ uuid }) => uuid === packet.uuid);
						const calculatedPrice = getCalculatedPrice(packet.uuid, calculated);

						return (
							<Slide key={packet.uuid}>
								<PremiumPacketProvider
									key={packet.uuid}
									packet={packet}
									onSelect={onSelect.bind(null, packet, isSelected, calculatedPrice)}
									isSelected={isSelected}
									isPricesLoading={!calculated.length}
								>
									<Packet isDisabled={isDisabled} showButton={type === 'summary' || isSubscriptionActive}>
										<PriceInfo calculatedPrice={calculatedPrice} modifiers="group" />
									</Packet>
								</PremiumPacketProvider>
							</Slide>
						);
					})}
				</PacketsSlider>
			</ModalContent>
		</AntModalStyled>
	);
});
